// UC 34264 — Master Recipe & Costed BOM Repository Page
// Mirrors RawHTMLDesign/recipe-&-bill-of-materials-repository/master-recipe-&-costed-bom-repository-page.html
// (Page Start / Page End block). Tabs are wired to /api/recipes + /api/master-data.
// Inputs / buttons carry data-testid for Playwright.
//
// Authorization: route is mounted behind ProtectedRoute(roles=[SuperAdmin,Finance]).
// Mutating buttons disable when canEdit is false. Backend enforces the same gate.

import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api.js';
import { useAuth } from '../../../utils/AuthContext.jsx';
import { ROLES } from '../../../utils/roles.js';
import './MasterRecipeAndCostedBomPage.css';

const ADMIN_ROLES = [ROLES.SUPER_ADMIN, ROLES.FINANCE];

const CATEGORIES = ['Broth', 'Protein', 'Produce Prep', 'Sauce', 'Dough'];

const EMPTY_LINE = () => ({ item_id: '', quantity: 1, unit: '', captured_unit_price: 0 });

const EMPTY_FORM = () => ({
  id: null,
  brand_id: '',
  sku_code: '',
  name: '',
  category: 'Broth',
  target_yield_qty: 1,
  yield_unit: 'Liters',
  owner_name: '',
  version_label: '',
  revision_notes: '',
  ingredients: [EMPTY_LINE()],
});

function FormBanner({ message, kind }) {
  if (!message) return null;
  return (
    <div
      className={`alert alert-${kind === 'error' ? 'danger' : 'success'} mt-3 mb-0`}
      data-testid={kind === 'error' ? 'recipe-error' : 'recipe-success'}
    >
      {message}
    </div>
  );
}

export default function MasterRecipeAndCostedBomPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole(...ADMIN_ROLES);

  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedFullRecipe, setSelectedFullRecipe] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [banner, setBanner] = useState({ message: '', kind: '' });
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('ALL');

  async function refresh() {
    const [b, i, r] = await Promise.all([
      api.get('/master-data/brands').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/items').then((x) => x.data.data || []).catch(() => []),
      api.get('/recipes').then((x) => x.data.data || []).catch(() => []),
    ]);
    setBrands(b);
    setItems(i);
    setRecipes(r);
  }

  useEffect(() => { refresh(); }, []);

  // selectedFullRecipe is fetched via GET /recipes/:id (includes all versions) when edit begins.
  // We keep it in sync with the catalog list after saves.

  // Derived cost from current form (live preview, also enforced server-side).
  const itemById = useMemo(
    () => Object.fromEntries(items.map((it) => [it.id, it])),
    [items],
  );
  const computedCost = useMemo(() => {
    let total = 0;
    for (const l of form.ingredients) {
      const it = itemById[l.item_id];
      const unitPrice = it ? Number(it.standard_unit_price) : Number(l.captured_unit_price || 0);
      total += Number(l.quantity || 0) * unitPrice;
    }
    return total;
  }, [form.ingredients, itemById]);

  function setField(name, value) { setForm((f) => ({ ...f, [name]: value })); }

  function setLine(idx, patch) {
    setForm((f) => {
      const next = [...f.ingredients];
      next[idx] = { ...next[idx], ...patch };
      // Auto-fill unit + captured price from item when item changes.
      if (patch.item_id) {
        const it = itemById[patch.item_id];
        if (it) {
          if (!next[idx].unit) next[idx].unit = it.uom;
          next[idx].captured_unit_price = Number(it.standard_unit_price);
        }
      }
      return { ...f, ingredients: next };
    });
  }

  function addLine() { setForm((f) => ({ ...f, ingredients: [...f.ingredients, EMPTY_LINE()] })); }
  function removeLine(idx) {
    setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  }

  function resetForm() {
    setForm(EMPTY_FORM());
    setSelectedFullRecipe(null);
    setBanner({ message: '', kind: '' });
  }

  async function startEdit(recipe) {
    setBanner({ message: '', kind: '' });
    const active = recipe.active_version;
    setForm({
      id: recipe.id,
      brand_id: recipe.brand_id,
      sku_code: recipe.sku_code || '',
      name: recipe.name,
      category: recipe.category,
      target_yield_qty: active ? Number(active.target_yield_qty) : 1,
      yield_unit: active ? active.yield_unit : 'Liters',
      owner_name: recipe.owner_name || '',
      version_label: '', // server bumps unless overridden
      revision_notes: '',
      ingredients: active && active.ingredients.length
        ? active.ingredients.map((l) => ({
            item_id: l.item_id,
            quantity: Number(l.quantity),
            unit: l.unit,
            captured_unit_price: Number(l.captured_unit_price),
          }))
        : [EMPTY_LINE()],
    });
    // Fetch full recipe (all versions) to populate history audit trail.
    try {
      const full = await api.get(`/recipes/${recipe.id}`);
      setSelectedFullRecipe(full.data.data);
    } catch (_) {
      setSelectedFullRecipe(null);
    }
  }

  async function save(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    const payload = {
      brand_id: form.brand_id,
      sku_code: form.sku_code || null,
      name: form.name,
      category: form.category,
      owner_name: form.owner_name || null,
      target_yield_qty: Number(form.target_yield_qty),
      yield_unit: form.yield_unit,
      version_label: form.version_label || undefined,
      revision_notes: form.revision_notes || null,
      ingredients: form.ingredients
        .filter((l) => l.item_id)
        .map((l) => ({
          item_id: l.item_id,
          quantity: Number(l.quantity),
          unit: l.unit,
        })),
    };
    try {
      if (form.id) {
        const savedId = form.id;
        const savedName = form.name;
        await api.put(`/recipes/${savedId}`, payload);
        // Reset form first so the catalog row re-renders without highlight,
        // then show the updated recipe in the history panel.
        resetForm();
        setBanner({ message: `Recipe "${savedName}" saved as new version.`, kind: 'success' });
        // Pre-load the full recipe so clicking Edit immediately shows history.
        const full = await api.get(`/recipes/${savedId}`);
        setSelectedFullRecipe(full.data.data);
      } else {
        await api.post('/recipes', payload);
        setBanner({ message: `Recipe "${form.name}" created.`, kind: 'success' });
      }
      await refresh();
      // Keep selection on the newly saved recipe for the history panel.
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  const filteredRecipes = recipes.filter((r) => {
    if (brandFilter !== 'ALL' && r.brand_id !== brandFilter) return false;
    if (search) {
      const hay = `${r.name} ${r.sku_code || ''}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const avgCost = filteredRecipes.length
    ? filteredRecipes.reduce((sum, r) => sum + Number(r.active_version?.standard_batch_cost || 0), 0)
      / filteredRecipes.length
    : 0;

  const latestVersion = recipes.reduce((acc, r) => {
    const v = r.active_version?.version_label;
    if (!v) return acc;
    return v > acc ? v : acc;
  }, '');

  return (
    <div className="container-fluid px-4 py-4 recipe-repository-page" data-testid="recipe-page">
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h1 className="h3 font-weight-bold text-dark mb-1">
              <i className="fa-solid fa-book-open-reader mr-2 text-primary" />
              Master Recipe &amp; Costed BOM Repository
            </h1>
            <p className="text-muted mb-0">
              Manage standard recipes, maintain BOM versions, model ingredient costs, and protect historical
              production records with versioned governance.
            </p>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card metric-card h-100 shadow-sm border-0">
            <div className="card-body">
              <small className="text-uppercase text-muted d-block mb-2">Recipes in Catalog</small>
              <div className="display-metric text-dark" data-testid="kpi-recipe-count">{filteredRecipes.length}</div>
              <small className="text-muted">Filtered active recipes</small>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card metric-card h-100 shadow-sm border-0">
            <div className="card-body">
              <small className="text-uppercase text-muted d-block mb-2">Average Standard Cost</small>
              <div className="display-metric text-dark" data-testid="kpi-average-cost">${avgCost.toFixed(2)}</div>
              <small className="text-muted">Across visible recipes</small>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card metric-card h-100 shadow-sm border-0">
            <div className="card-body">
              <small className="text-uppercase text-muted d-block mb-2">Draft Changes Pending</small>
              <div className="display-metric text-warning" data-testid="kpi-draft-changes">
                {form.id || form.name ? 1 : 0}
              </div>
              <small className="text-muted">Unsaved BOM edits</small>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card metric-card h-100 shadow-sm border-0">
            <div className="card-body">
              <small className="text-uppercase text-muted d-block mb-2">Latest Active Version</small>
              <div className="display-metric text-dark" data-testid="kpi-top-version">{latestVersion || '--'}</div>
              <small className="text-muted">Most recently touched recipe version</small>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-5 col-lg-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="card-title mb-0 font-weight-bold text-teal">
                <i className="fa-solid fa-sliders mr-2" />Recipe Configuration Workspace
              </h5>
              <span className="badge badge-light border" data-testid="editing-recipe-badge">
                {form.id ? `Editing: ${form.name}` : 'No recipe selected'}
              </span>
            </div>
            <div className="card-body">
              <form onSubmit={save} noValidate data-testid="recipe-form">
                <div className="form-row">
                  <div className="form-group col-md-7">
                    <label htmlFor="recipe-brand" className="font-weight-600 text-dark">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <select
                      id="recipe-brand"
                      className="form-control custom-select"
                      value={form.brand_id}
                      onChange={(e) => setField('brand_id', e.target.value)}
                      data-testid="recipe-brand"
                      required
                    >
                      <option value="" disabled>Select brand...</option>
                      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group col-md-5">
                    <label htmlFor="recipe-sku-code" className="font-weight-600 text-dark">SKU Code</label>
                    <input
                      id="recipe-sku-code"
                      className="form-control"
                      value={form.sku_code}
                      onChange={(e) => setField('sku_code', e.target.value)}
                      data-testid="recipe-sku-code"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="recipe-name" className="font-weight-600 text-dark">
                    Recipe Name <span className="text-danger">*</span>
                  </label>
                  <input
                    id="recipe-name"
                    className="form-control"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    data-testid="recipe-name"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group col-md-4">
                    <label htmlFor="recipe-category" className="font-weight-600 text-dark">Category</label>
                    <select
                      id="recipe-category"
                      className="form-control custom-select"
                      value={form.category}
                      onChange={(e) => setField('category', e.target.value)}
                      data-testid="recipe-category"
                    >
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group col-md-4">
                    <label htmlFor="recipe-yield-qty" className="font-weight-600 text-dark">
                      Yield Qty <span className="text-danger">*</span>
                    </label>
                    <input
                      id="recipe-yield-qty"
                      type="number"
                      min="0"
                      step="0.1"
                      className="form-control"
                      value={form.target_yield_qty}
                      onChange={(e) => setField('target_yield_qty', e.target.value)}
                      data-testid="recipe-yield-qty"
                      required
                    />
                  </div>
                  <div className="form-group col-md-4">
                    <label htmlFor="recipe-yield-unit" className="font-weight-600 text-dark">Yield Unit</label>
                    <input
                      id="recipe-yield-unit"
                      className="form-control"
                      value={form.yield_unit}
                      onChange={(e) => setField('yield_unit', e.target.value)}
                      data-testid="recipe-yield-unit"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label htmlFor="recipe-version" className="font-weight-600 text-dark">Version Label</label>
                    <input
                      id="recipe-version"
                      className="form-control"
                      placeholder={form.id ? '(auto-bumps on save)' : 'v1.0'}
                      value={form.version_label}
                      onChange={(e) => setField('version_label', e.target.value)}
                      data-testid="recipe-version"
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label htmlFor="recipe-owner" className="font-weight-600 text-dark">Recipe Owner</label>
                    <input
                      id="recipe-owner"
                      className="form-control"
                      value={form.owner_name}
                      onChange={(e) => setField('owner_name', e.target.value)}
                      data-testid="recipe-owner"
                    />
                  </div>
                </div>
                <div className="form-group mb-4">
                  <label htmlFor="recipe-notes" className="font-weight-600 text-dark">Revision Notes</label>
                  <textarea
                    id="recipe-notes"
                    rows="2"
                    className="form-control"
                    value={form.revision_notes}
                    onChange={(e) => setField('revision_notes', e.target.value)}
                    data-testid="recipe-notes"
                  />
                </div>

                <div className="bom-workspace border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                    <h6 className="font-weight-bold text-dark mb-0">
                      <i className="fa-solid fa-diagram-project mr-1 text-primary" />
                      Ingredients &amp; BOM Breakdown
                    </h6>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addLine}
                      data-testid="btn-add-bom-row"
                      disabled={!canEdit}
                    >
                      <i className="fa-solid fa-plus mr-1" />Add Ingredient
                    </button>
                  </div>
                  <div className="table-responsive bom-table-wrap">
                    <table className="table table-sm table-hover mb-0 text-center" data-testid="bom-table">
                      <thead>
                        <tr>
                          <th className="text-left">Ingredient</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Unit Cost</th>
                          <th>Line Cost</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.ingredients.map((l, idx) => {
                          const it = itemById[l.item_id];
                          const unitPrice = it ? Number(it.standard_unit_price) : Number(l.captured_unit_price || 0);
                          const lineCost = Number(l.quantity || 0) * unitPrice;
                          return (
                            <tr key={idx}>
                              <td className="text-left">
                                <select
                                  className="form-control form-control-sm"
                                  value={l.item_id}
                                  onChange={(e) => setLine(idx, { item_id: e.target.value })}
                                  data-testid={`bom-item-${idx}`}
                                >
                                  <option value="" disabled>Select ingredient...</option>
                                  {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  value={l.quantity}
                                  onChange={(e) => setLine(idx, { quantity: e.target.value })}
                                  data-testid={`bom-qty-${idx}`}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control form-control-sm"
                                  value={l.unit}
                                  onChange={(e) => setLine(idx, { unit: e.target.value })}
                                  data-testid={`bom-unit-${idx}`}
                                />
                              </td>
                              <td>${unitPrice.toFixed(2)}</td>
                              <td data-testid={`bom-line-cost-${idx}`}>${lineCost.toFixed(2)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeLine(idx)}
                                  data-testid={`bom-remove-${idx}`}
                                >
                                  <i className="fa-solid fa-trash" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="cost-summary border rounded p-3 mb-3">
                  <div className="row text-center">
                    <div className="col-4 border-right">
                      <small className="text-muted d-block">Ingredients</small>
                      <div className="font-weight-bold text-dark h5 mb-0" data-testid="cost-summary-lines">
                        {form.ingredients.filter((l) => l.item_id).length}
                      </div>
                    </div>
                    <div className="col-4 border-right">
                      <small className="text-muted d-block">Total Standard Cost</small>
                      <div className="font-weight-bold text-teal h5 mb-0" data-testid="cost-summary-total">
                        ${computedCost.toFixed(2)}
                      </div>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Cost / Yield Unit</small>
                      <div className="font-weight-bold text-dark h5 mb-0" data-testid="cost-summary-per-unit">
                        ${form.target_yield_qty > 0 ? (computedCost / Number(form.target_yield_qty)).toFixed(4) : '0.00'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap">
                  <button
                    type="submit"
                    className="btn btn-primary mr-2 mb-2"
                    data-testid="btn-save-recipe"
                    disabled={!canEdit}
                  >
                    <i className="fa-solid fa-floppy-disk mr-1" />Save Version
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary mb-2"
                    onClick={resetForm}
                    data-testid="btn-reset-recipe"
                  >
                    <i className="fa-solid fa-rotate-left mr-1" />Reset Form
                  </button>
                </div>
                <FormBanner message={banner.message} kind={banner.kind} />
              </form>
            </div>
          </div>
        </div>

        <div className="col-xl-7 col-lg-6 mb-4">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="card-title mb-0 font-weight-bold text-dark">
                <i className="fa-solid fa-table-list mr-2 text-primary" />Recipe Catalog
              </h5>
              <div className="d-flex align-items-center flex-wrap mt-2 mt-sm-0">
                <div className="input-group input-group-sm mr-2 mb-2 mb-sm-0 recipe-search-box">
                  <div className="input-group-prepend">
                    <span className="input-group-text bg-white"><i className="fa-solid fa-search text-muted" /></span>
                  </div>
                  <input
                    className="form-control"
                    placeholder="Search recipe or SKU..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="recipe-search"
                  />
                </div>
                <select
                  className="form-control form-control-sm custom-select mb-2 mb-sm-0"
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  data-testid="recipe-brand-filter"
                >
                  <option value="ALL">All Brands</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive recipe-table-wrap">
                <table className="table table-hover mb-0 text-center" data-testid="recipe-table">
                  <thead>
                    <tr>
                      <th className="text-left">Recipe / SKU</th>
                      <th>Brand</th>
                      <th>Category</th>
                      <th>Target Yield</th>
                      <th>Version</th>
                      <th>Total Cost</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecipes.length === 0 && (
                      <tr><td colSpan="7" className="text-muted py-4" data-testid="recipe-empty">No recipes yet.</td></tr>
                    )}
                    {filteredRecipes.map((r) => {
                      const v = r.active_version;
                      return (
                        <tr key={r.id} className={form.id === r.id ? 'row-highlight' : ''} data-testid={`recipe-row-${r.id}`}>
                          <td className="text-left">
                            <div className="font-weight-bold text-dark">{r.name}</div>
                            {r.sku_code && <small className="text-muted">{r.sku_code}</small>}
                          </td>
                          <td>{r.brand_name}</td>
                          <td>{r.category}</td>
                          <td>{v ? `${v.target_yield_qty} ${v.yield_unit}` : '--'}</td>
                          <td>
                            <span className="version-pill status-active-version" data-testid={`recipe-version-${r.id}`}>
                              {v?.version_label || '--'}
                            </span>
                          </td>
                          <td data-testid={`recipe-cost-${r.id}`}>
                            ${Number(v?.standard_batch_cost || 0).toFixed(2)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => startEdit(r)}
                              data-testid={`recipe-edit-${r.id}`}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="card-title mb-0 font-weight-bold text-dark">
                <i className="fa-solid fa-clock-rotate-left mr-2 text-primary" />Recipe Version History Audit Trail
              </h5>
              <span className="small text-muted">Legacy versions remain immutable</span>
            </div>
            <div className="card-body">
              <div className="table-responsive history-table-wrap">
                <table className="table table-sm table-hover mb-0 text-center" data-testid="history-table">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th className="text-left">Edited By</th>
                      <th className="text-left">Change Summary</th>
                      <th>Timestamp</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedFullRecipe && (
                      <tr><td colSpan="5" className="text-muted py-4" data-testid="history-empty">
                        Select a recipe to view its version history.
                      </td></tr>
                    )}
                    {selectedFullRecipe?.versions?.map((v) => (
                      <tr key={v.id} data-testid={`history-row-${v.id}`}>
                        <td>
                          <span className={`version-pill ${v.is_active ? 'status-active-version' : 'status-archived-version'}`}>
                            {v.version_label}
                          </span>
                        </td>
                        <td className="text-left">{v.edited_by_label || '--'}</td>
                        <td className="text-left">{v.revision_notes || '--'}</td>
                        <td><small>{new Date(v.edited_at).toLocaleString()}</small></td>
                        <td>
                          <span className={`badge ${v.is_active ? 'badge-success' : 'badge-secondary'}`}
                            data-testid={`history-status-${v.id}`}
                          >
                            {v.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
